import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  themeColors,
  useDialog,
  useGlobalContext
} from '@applications-terrains/birdz-react-library';
import { useTranslation } from 'react-i18next';
import ConfirmDialog, { ConfirmDialogOptions } from '../../ConfirmDialog';
import { useInterventionCreation } from '../../context/interventionCreationContext';
import BackdropWithLoader from '../../../../../Tools/BackdropWithLoader/BackdropWithLoader';
import BatchErrorModal from '../../BatchErrorModal';
import TablePagination from '../../../../../Tools/Table/TablePagination';
import { DEFAULT_PAGE_SIZE, clampPage, pageRows } from '../../../../../Tools/Table/pagination';
import { ACTION_NEEDED_CHIP_SX, NEW_CHIP_SX, PREFILLED_CHIP_SX, selectedRowSx } from '../../theme';
import RequestDetail from './RequestDetail';
import {
  RequestRow,
  RequestSortColumn,
  SortDirection,
  canSendToAssignment,
  configurationLabel,
  displayValue,
  formatAddress,
  getQualificationState,
  missingQualificationKey,
  sortRequests
} from './requestRows';
import { useDeleteRequest } from './useDeleteRequest';
import { useSendToAssignment } from './useSendToAssignment';
import { useInterventionTypeLabel } from './useInterventionTypeLabel';

type RequestsTableProps = {
  rows: RequestRow[];
  isLoading: boolean;
};

export default function RequestsTable({ rows, isLoading }: RequestsTableProps) {
  const { t } = useTranslation();
  const { state, dispatch } = useInterventionCreation();
  const { deleteRequest, deleteRequests, isDeleting } = useDeleteRequest();
  const { sendToAssignment, isSending, hasFailed, failureReason, dismissError } =
    useSendToAssignment();
  const { confirmDialog, closeDialog, dialogOptions } = useDialog();
  const qualificationLabel = useInterventionTypeLabel();

  const [sortColumn, setSortColumn] = useState<RequestSortColumn>('servicePoint');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const sortedRows = useMemo(
    () => sortRequests(rows, sortColumn, sortDirection),
    [rows, sortColumn, sortDirection]
  );

  // Dérivée plutôt que synchronisée par effet : la liste raccourcit après un envoi
  // ou une suppression, et la page courante peut alors ne plus exister.
  const currentPage = clampPage(page, rows.length, pageSize);
  const visibleRows = pageRows(sortedRows, currentPage, pageSize);

  const selectedCount = state.selectedRequestIds.length;

  /**
   * L'envoi s'ouvre dès qu'une demande cochée est qualifiée ou pré-remplie.
   *
   * Une sélection dont aucune ne l'est reviendrait intégralement en échec. On ne bloque
   * pas pour autant une sélection mixte : le back tranche ligne par ligne et les refus
   * remontent dans le bilan.
   *
   * Lu sur les lignes affichées : une demande cochée puis masquée par un filtre n'est plus
   * évaluable, et le bouton se ferme alors plutôt que de promettre un envoi invisible.
   */
  const hasSendableSelection = rows.some(
    (row) => state.selectedRequestIds.includes(row.id) && canSendToAssignment(row)
  );

  // Teinte de la ligne cochée : l'accent du thème choisi par l'utilisateur.
  const { theme } = useGlobalContext();
  const selectedSx = selectedRowSx(themeColors[theme]);

  /** Suppression irréversible : elle passe par une confirmation, comme en v1. */
  const askToDeleteSelected = () => {
    const options: ConfirmDialogOptions = {
      title: t('interventionCreation.requests.deleteSelectedConfirmTitle'),
      content: t('interventionCreation.requests.deleteSelectedConfirmContent', {
        count: selectedCount
      }),
      onValidate: () => {
        // Fermer avant de lancer : deux modales superposées si la mutation échoue.
        closeDialog();
        deleteRequests(state.selectedRequestIds);
      },
      onCancel: () => closeDialog()
    };

    confirmDialog(options);
  };

  const toggleSort = (column: RequestSortColumn) => {
    if (column === sortColumn) {
      setSortDirection((previous) => (previous === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortColumn(column);
    setSortDirection('asc');
  };

  const allSelected = rows.length > 0 && state.selectedRequestIds.length === rows.length;

  const toggleAll = () =>
    dispatch({
      type: 'setSelectedRequests',
      ids: allSelected ? [] : rows.map((row) => row.id)
    });

  // Ce retour anticipé démonte le voile et la modale posés plus bas. Il reste hors de
  // portée pendant un envoi : react-query sert les données précédentes le temps du
  // refetch, la liste ne se vide donc jamais avant la fin de l'appel.
  if (!isLoading && rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
        {t('interventionCreation.requests.empty')}
      </Typography>
    );
  }

  return (
    <>
      <ConfirmDialog options={dialogOptions} />

      <BackdropWithLoader
        open={isSending}
        message={t('interventionCreation.requests.sendingToAssignment')}
      />

      <BatchErrorModal
        open={hasFailed}
        message={t('interventionCreation.requests.sendToAssignmentError')}
        reason={failureReason}
        onClose={dismissError}
      />

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={allSelected}
                  indeterminate={state.selectedRequestIds.length > 0 && !allSelected}
                  onChange={toggleAll}
                  inputProps={{ 'aria-label': t('interventionCreation.requests.selectAll') }}
                />
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortColumn === 'servicePoint'}
                  direction={sortColumn === 'servicePoint' ? sortDirection : 'asc'}
                  onClick={() => toggleSort('servicePoint')}
                >
                  {t('interventionCreation.requests.columns.servicePoint')}
                </TableSortLabel>
              </TableCell>
              <TableCell>{t('interventionCreation.requests.columns.sensor')}</TableCell>
              <TableCell>{t('interventionCreation.requests.columns.module')}</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortColumn === 'qualification'}
                  direction={sortColumn === 'qualification' ? sortDirection : 'asc'}
                  onClick={() => toggleSort('qualification')}
                >
                  {t('interventionCreation.requests.columns.qualification')}
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                {t('interventionCreation.requests.columns.actions')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((row) => {
              const isExpanded = expandedId === row.id;
              const isSelected = state.selectedRequestIds.includes(row.id);
              const isNew = state.newRequestIds.includes(row.id);
              const qualificationState = getQualificationState(row);
              const missingKey = missingQualificationKey(row);

              return (
                <React.Fragment key={row.id}>
                  <TableRow hover sx={isSelected ? selectedSx : undefined}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isSelected}
                        onChange={() => dispatch({ type: 'toggleRequest', id: row.id })}
                        inputProps={{
                          'aria-label': t('interventionCreation.requests.selectRow', {
                            name: displayValue(row.service_point_customer_id)
                          })
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {/* La puce est posée au-dessus du bloc, et non à sa gauche : entre la case
                          à cocher et le numéro, elle flottait sans appartenir à l'un ni à l'autre,
                          et elle décalait le numéro des seules lignes neuves — la colonne partait
                          en dents de scie. Sur sa propre ligne, les numéros s'alignent d'une ligne
                          à l'autre et la largeur de la puce n'entre plus en jeu, ce qui compte :
                          elle n'a pas la même largeur d'une langue à l'autre. */}
                      <Stack spacing={0.5} alignItems="flex-start">
                        {isNew && (
                          <Chip
                            size="small"
                            sx={NEW_CHIP_SX}
                            label={t('interventionCreation.requests.new')}
                          />
                        )}
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2" fontWeight={600}>
                            {displayValue(row.service_point_customer_id)}
                          </Typography>
                          {row.contract_data?.oss_code && (
                            <Chip size="small" label={row.contract_data.oss_code} />
                          )}
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          {formatAddress(row)}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.5} alignItems="flex-start">
                        <Typography variant="body2">{displayValue(row.equipment_id)}</Typography>
                        {row.equipment_family_type && (
                          <Chip size="small" label={row.equipment_family_type} />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.5} alignItems="flex-start">
                        <Typography variant="body2">{displayValue(row.device_id)}</Typography>
                        <Stack direction="row" spacing={0.5}>
                          {row.device_model && <Chip size="small" label={row.device_model} />}
                          {row.device_radio_mode && (
                            <Chip size="small" label={row.device_radio_mode} />
                          )}
                        </Stack>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.5} alignItems="flex-start">
                        {qualificationState === 'toQualify' && (
                          <Chip
                            size="small"
                            color="info"
                            label={t('interventionCreation.requests.toQualify')}
                          />
                        )}
                        {qualificationState === 'partial' && (
                          <Chip
                            size="small"
                            sx={ACTION_NEEDED_CHIP_SX}
                            label={t('interventionCreation.requests.incomplete')}
                          />
                        )}
                        {/* L'import a tout rempli, mais personne n'a qualifié : ni le bleu
                            de l'attente, ni le vert du terminé. */}
                        {qualificationState === 'prefilled' && (
                          <Chip
                            size="small"
                            sx={PREFILLED_CHIP_SX}
                            label={t('interventionCreation.requests.prefilled')}
                          />
                        )}
                        {/* Le vert de la charte est porté par `secondary`, seule couleur
                            « terminé » de la palette augmentée. */}
                        {qualificationState === 'complete' && (
                          <Chip
                            size="small"
                            color="secondary"
                            label={t('interventionCreation.requests.qualified')}
                          />
                        )}
                        {/* On montre ce qui est posé, quel que soit l'état : la puce dit
                            l'avancement, ces deux lignes disent le contenu. Chacune est
                            gardée sur son propre champ, sans quoi elle se réduirait au
                            tiret de remplacement. */}
                        {row.intervention_type_code != null && (
                          <Typography variant="body2">{qualificationLabel(row)}</Typography>
                        )}
                        {row.configuration_data != null && (
                          <Typography variant="body2" color="text.secondary">
                            {configurationLabel(row, t)}
                          </Typography>
                        )}
                        {/* Une mention discrète : elle complète la puce sans lui disputer
                            la lecture, d'où `caption` plutôt que `body2`. */}
                        {missingKey !== null && (
                          <Typography variant="caption" color="text.secondary">
                            {t(missingKey)}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip
                        title={t(
                          isExpanded
                            ? 'interventionCreation.requests.collapseRow'
                            : 'interventionCreation.requests.expandRow'
                        )}
                      >
                        <IconButton
                          size="small"
                          aria-label={t(
                            isExpanded
                              ? 'interventionCreation.requests.collapseRow'
                              : 'interventionCreation.requests.expandRow'
                          )}
                          onClick={() => setExpandedId(isExpanded ? null : row.id)}
                        >
                          {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      </Tooltip>
                      {/* Un bouton désactivé n'émet pas d'événement de souris : sans ce
                          `span`, l'infobulle ne s'afficherait pas pendant la suppression. */}
                      <Tooltip title={t('interventionCreation.requests.deleteRow')}>
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            disabled={isDeleting}
                            aria-label={t('interventionCreation.requests.deleteRow')}
                            onClick={() => deleteRequest(row.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  {/* Même teinte que la ligne principale : dépliée et cochée, l'ensemble
                      doit se lire comme un seul bloc. */}
                  <TableRow sx={isSelected ? selectedSx : undefined}>
                    <TableCell colSpan={6} sx={{ py: 0, border: isExpanded ? undefined : 'none' }}>
                      <Collapse in={isExpanded} unmountOnExit>
                        <Box>
                          <RequestDetail row={row} />
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        total={rows.length}
        selectedCount={state.selectedRequestIds.length}
        page={currentPage}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        action={
          // Les deux actions partagent la même sélection : chacune neutralise l'autre
          // pendant son appel, sinon la seconde partirait avec des identifiants que la
          // première est en train de faire quitter la liste.
          <Stack direction="row" spacing={1.5}>
            <Button
              variant="outlined"
              color="error"
              disabled={selectedCount === 0 || isDeleting || isSending}
              onClick={askToDeleteSelected}
            >
              {t('interventionCreation.requests.deleteSelected', { total: selectedCount })}
            </Button>
            <Button
              variant="contained"
              disabled={selectedCount === 0 || isDeleting || isSending || !hasSendableSelection}
              onClick={sendToAssignment}
            >
              {t('interventionCreation.requests.sendToAssignment', { total: selectedCount })}
            </Button>
          </Stack>
        }
      />
    </>
  );
}
